import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Fallback art for the fraud pages when there is nothing to show. Inline SVG,
 * so it needs no network and scales cleanly. Flat soft tones, matching the
 * report illustrations (report/reportIllustrations.tsx).
 *
 * - alerts:   no alerts yet (all clear)
 * - filtered: alerts exist, but none match the filters
 * - rules:    no fraud rules yet
 * - chart:    a breakdown panel with nothing unresolved
 * - error:    the API could not be reached
 */
export type FraudEmptyVariant = "alerts" | "filtered" | "rules" | "chart" | "error";

const GREEN = { bg: "#ecfdf5", wash: "#d1fae5", mid: "#34d399", strong: "#059669" };
const SLATE = { bg: "#f8fafc", wash: "#e2e8f0", mid: "#94a3b8", strong: "#475569" };
const RED = { bg: "#fef2f2", wash: "#fee2e2", strong: "#dc2626" };

function AlertsClearArt() {
  return (
    <svg viewBox="0 0 160 120" className="h-full w-full" aria-hidden="true">
      <circle cx="80" cy="60" r="52" fill={GREEN.bg} />
      <circle cx="128" cy="24" r="10" fill={GREEN.wash} />
      <circle cx="30" cy="96" r="7" fill={GREEN.wash} />
      {/* Shield */}
      <path d="M80 24 L108 34 V58 C108 76 96 89 80 96 C64 89 52 76 52 58 V34 Z" fill="#ffffff" stroke={GREEN.mid} strokeWidth="3" />
      <path d="M80 32 L101 40 V58 C101 72 92 82 80 88 Z" fill={GREEN.wash} />
      {/* Check */}
      <path d="M67 60 L76 69 L94 50" fill="none" stroke={GREEN.strong} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Sparkles */}
      <path d="M120 54 l3 -7 l3 7 l7 3 l-7 3 l-3 7 l-3 -7 l-7 -3 z" fill={GREEN.mid} />
      <path d="M38 40 l2 -4 l2 4 l4 2 l-4 2 l-2 4 l-2 -4 l-4 -2 z" fill={GREEN.mid} />
    </svg>
  );
}

function FilteredArt() {
  return (
    <svg viewBox="0 0 160 120" className="h-full w-full" aria-hidden="true">
      <circle cx="80" cy="60" r="52" fill={SLATE.bg} />
      {/* Empty list card */}
      <rect x="40" y="28" width="64" height="70" rx="8" fill="#ffffff" stroke={SLATE.wash} strokeWidth="2" />
      <rect x="50" y="40" width="34" height="6" rx="3" fill={SLATE.wash} />
      <rect x="50" y="54" width="44" height="6" rx="3" fill={SLATE.wash} />
      <rect x="50" y="68" width="26" height="6" rx="3" fill={SLATE.wash} />
      {/* Magnifier */}
      <circle cx="104" cy="74" r="16" fill="#ffffff" stroke={SLATE.strong} strokeWidth="4" />
      <path d="M97 74 h14" stroke={SLATE.mid} strokeWidth="3" strokeLinecap="round" />
      <path d="M116 86 L128 98" stroke={SLATE.strong} strokeWidth="5" strokeLinecap="round" />
    </svg>
  );
}

function RulesArt() {
  return (
    <svg viewBox="0 0 160 120" className="h-full w-full" aria-hidden="true">
      <circle cx="80" cy="60" r="52" fill={GREEN.bg} />
      <circle cx="30" cy="28" r="8" fill={GREEN.wash} />
      {/* Rule sheet */}
      <rect x="46" y="22" width="60" height="78" rx="8" fill="#ffffff" stroke={SLATE.wash} strokeWidth="2" />
      {/* Toggle rows: one on, two off */}
      <rect x="56" y="36" width="18" height="10" rx="5" fill={GREEN.mid} />
      <circle cx="69" cy="41" r="4" fill="#ffffff" />
      <rect x="80" y="38" width="18" height="6" rx="3" fill={SLATE.wash} />
      <rect x="56" y="56" width="18" height="10" rx="5" fill={SLATE.wash} />
      <circle cx="61" cy="61" r="4" fill="#ffffff" />
      <rect x="80" y="58" width="14" height="6" rx="3" fill={SLATE.wash} />
      <rect x="56" y="76" width="18" height="10" rx="5" fill={SLATE.wash} />
      <circle cx="61" cy="81" r="4" fill="#ffffff" />
      <rect x="80" y="78" width="18" height="6" rx="3" fill={SLATE.wash} />
      {/* Plus badge */}
      <circle cx="110" cy="92" r="14" fill={GREEN.strong} />
      <path d="M110 85 v14 M103 92 h14" stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" />
    </svg>
  );
}

function ChartArt() {
  return (
    <svg viewBox="0 0 160 80" className="h-full w-full" aria-hidden="true">
      <rect x="20" y="64" width="120" height="2" rx="1" fill={SLATE.wash} />
      {[30, 54, 78, 102].map((x, index) => (
        <rect key={x} x={x} y={58 - index} width="16" height={6 + index} rx="3" fill={SLATE.wash} />
      ))}
      <circle cx="124" cy="26" r="12" fill={GREEN.wash} />
      <path d="M118 26 L122 30 L130 22" fill="none" stroke={GREEN.strong} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ErrorArt() {
  return (
    <svg viewBox="0 0 160 120" className="h-full w-full" aria-hidden="true">
      <circle cx="80" cy="60" r="52" fill={RED.bg} />
      {/* Unplugged cable */}
      <path d="M30 70 C46 70 46 52 62 52" fill="none" stroke={SLATE.mid} strokeWidth="4" strokeLinecap="round" />
      <rect x="60" y="44" width="16" height="16" rx="3" fill={SLATE.strong} />
      <path d="M76 48 h6 M76 56 h6" stroke={SLATE.strong} strokeWidth="3" strokeLinecap="round" />
      <rect x="94" y="44" width="16" height="16" rx="3" fill={SLATE.mid} />
      <path d="M110 52 C122 52 122 72 134 72" fill="none" stroke={SLATE.mid} strokeWidth="4" strokeLinecap="round" />
      {/* Spark in the gap */}
      <path d="M88 36 l-3 7 h5 l-3 7" fill="none" stroke={RED.strong} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Warning dot */}
      <circle cx="80" cy="88" r="9" fill={RED.wash} />
      <path d="M80 83 v6" stroke={RED.strong} strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="80" cy="92.5" r="1.5" fill={RED.strong} />
    </svg>
  );
}

const ART: Record<FraudEmptyVariant, () => JSX.Element> = {
  alerts: AlertsClearArt,
  filtered: FilteredArt,
  rules: RulesArt,
  chart: ChartArt,
  error: ErrorArt,
};

interface FraudEmptyStateProps {
  variant: FraudEmptyVariant;
  title: string;
  description?: string;
  /** A button or link under the text. */
  action?: ReactNode;
  /** Smaller art and text, for side panels. */
  compact?: boolean;
  className?: string;
}

export function FraudEmptyState({ variant, title, description, action, compact, className }: FraudEmptyStateProps) {
  const Art = ART[variant];
  return (
    <div
      role="status"
      className={cn("flex flex-col items-center text-center", compact ? "gap-2 py-3" : "gap-3 py-10", className)}
    >
      <div className={cn(compact ? "h-14 w-28" : "h-28 w-36")}>
        <Art />
      </div>
      <div className="max-w-sm space-y-1">
        <p className={cn("font-medium text-slate-900", compact ? "text-sm" : "text-base")}>{title}</p>
        {description && <p className={cn("text-muted-foreground", compact ? "text-xs" : "text-sm")}>{description}</p>}
      </div>
      {action}
    </div>
  );
}
